import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { Button, Input, Spinner, Stat, ZoneChip } from "../components/ui";
import { Crosshair, ChevronDown, ChevronRight, AlertTriangle, Ban, Link2, ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useRateDefault, rateHint } from "../lib/useRateDefault";

// Bewerter P1 — form in, dossier out. HARD RULE (concept §10 P1 gate): this
// page contains ZERO calculation. Every number below is rendered verbatim from
// POST /api/analyze (which runs the same bewerten.py engine as the CLI).
// Helpers here format only (thousands grouping), never compute.

const eur = (v) => (v == null ? "—" : `€${Number(v).toLocaleString("de-AT")}`);

const AMPEL_STYLE = {
  "GRÜN": "text-signal-green border-signal-greenBorder bg-signal-greenSoft",
  "PRÜFEN": "text-signal-yellow border-signal-yellowBorder bg-signal-yellowSoft",
  "NO-GO": "text-signal-red border-signal-redBorder bg-signal-redSoft",
};

const EMPTY = {
  price: "", size_m2: "", market: "", postcode: "", address: "",
  objekt_typ: "etw", anzahl_we: "", jahresnettokaltmiete: "",
  leerstand_m2: "", gewerbe_m2: "",
  rent_actual_mo: "", vermietet: false, unbefristet: false, befristet_bis: "",
  seller: "makler", baujahr: "", energieklasse: "",
  hausgeld_mo: "", ihr_mo: "", verwaltung_mo: "", bk_voraus_mo: "", anteil: "",
  sonderumlagen: "", reno_cost: "", restnutzwert_jahre: "", erbbaurecht: false,
  stockwerk: "", aufzug: "", beschreibung: "",
  country: "", lage: "",
};

// Profile fields stay empty by default — the ENGINE's defaults rule (single
// source of truth in bewerten.InvestorProfile); placeholders just show them.
const EMPTY_PROFILE = {
  rate: "", ek_anteil: "", term_years: "", steuersatz: "",
  ek_available: "", ziel: "", haltedauer_jahre: "", gmbh: false,
};

const NUM_FIELDS = ["price", "size_m2", "rent_actual_mo", "baujahr", "hausgeld_mo",
  "ihr_mo", "verwaltung_mo", "bk_voraus_mo", "anteil", "sonderumlagen", "reno_cost",
  "restnutzwert_jahre", "anzahl_we", "jahresnettokaltmiete", "leerstand_m2",
  "gewerbe_m2", "stockwerk"];
const STR_FIELDS = ["market", "postcode", "address", "seller", "energieklasse",
  "befristet_bis", "country", "lage", "beschreibung"];
const PROFILE_NUM = ["rate", "ek_anteil", "term_years", "steuersatz", "ek_available",
  "haltedauer_jahre"];
const DOC_TYPES = [
  "Energieausweis",
  "Grundbuch",
  "Mietvertrag",
  "Nutzwertgutachten",
  "Objektabrechnungen",
  "Bestandspläne",
  "ETV-Protokolle",
  "Monatsvorschreibungen",
  "Wohnungseigentumsvertrag",
];

function buildPayload(form, profile) {
  const p = {};
  NUM_FIELDS.forEach((f) => {
    if (form[f] !== "" && form[f] != null) {
      const v = Number(form[f]);
      if (!Number.isNaN(v)) p[f] = v;
    }
  });
  STR_FIELDS.forEach((f) => { if (form[f]) p[f] = form[f]; });
  ["vermietet", "unbefristet", "erbbaurecht"].forEach((f) => { if (form[f]) p[f] = true; });
  if (form.aufzug) p.aufzug = form.aufzug === "ja";
  if (form.objekt_typ === "mfh") p.objekt_typ = "mfh";
  const prof = {};
  PROFILE_NUM.forEach((f) => {
    if (profile[f] !== "" && profile[f] != null) {
      const v = Number(profile[f]);
      if (!Number.isNaN(v)) prof[f] = v;
    }
  });
  if (profile.ziel) prof.ziel = profile.ziel;
  if (profile.gmbh) prof.gmbh = true;
  if (Object.keys(prof).length) p.profile = prof;
  return p;
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[10px] text-text-muted">{hint}</span>}
    </label>
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none"
    >
      {children}
    </select>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs font-mono text-text-secondary cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="accent-white" />
      {label}
    </label>
  );
}

function Section({ title, open, onToggle, children }) {
  return (
    <div className="border border-border">
      <button type="button" onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left overline hover:bg-surfaceHover">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {title}
      </button>
      {open && <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-border">{children}</div>}
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 border-b border-border/50 last:border-0">
      <span className="text-xs text-text-muted">{k}</span>
      <span className={`num text-sm text-right ${accent || "text-white"}`}>{v}</span>
    </div>
  );
}

function Dossier({ r }) {
  const fin = r.finanzierung, stx = r.stress_anchor, emp = r.empfehlung, k = r.kennzahlen;
  const ampelCls = AMPEL_STYLE[r.ampel] || AMPEL_STYLE["NO-GO"];
  const cfAccent = fin.cf_pretax_mo >= 0 ? "green" : "red";
  return (
    <div className="space-y-4" data-testid="bewerter-dossier">
      {r.degraded && (
        <div className="border border-signal-yellowBorder bg-signal-yellowSoft text-signal-yellow px-4 py-3 text-xs font-mono flex items-center gap-2">
          <AlertTriangle size={14} />
          MARKT NICHT KALIBRIERT — Miete manuell, Konfidenz NIEDRIG, Makro nur Grobnote
        </div>
      )}

      <div className="border border-border bg-surface p-5 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div>
          <div className="overline">Deal Score</div>
          <div className={`num text-6xl font-light ${ampelCls.split(" ")[0]}`}>{r.score}</div>
        </div>
        <span className={`border px-3 py-1 font-mono text-sm font-bold tracking-widest2 ${ampelCls}`}>
          {r.ampel}
        </span>
        <ZoneChip zone={r.zone === "GREEN_BORDER" ? "GREEN_BORDER" : r.zone} size="lg" />
        <div className="text-xs font-mono text-text-muted">
          Lage {r.lage} · Konfidenz {r.confidence}
          {r.stats_source ? ` · Marktdaten: ${r.stats_source}` : " · kein Marktwertvergleich"}
        </div>
      </div>

      {r.kos.length > 0 && (
        <div className="border border-signal-redBorder bg-signal-redSoft px-4 py-3 space-y-1">
          {r.kos.map((ko, i) => (
            <div key={i} className="text-xs font-mono text-signal-red flex items-center gap-2">
              <Ban size={13} /> K.O.: {ko}
            </div>
          ))}
        </div>
      )}

      <div className="border border-border bg-surface p-4">
        <div className="overline mb-2">Score-Zusammensetzung</div>
        {r.breakdown.map((c) => (
          <div key={c.name} className="flex items-baseline gap-3 py-1 border-b border-border/50 last:border-0">
            <span className="num text-xs w-16 shrink-0 text-right">{c.pts}/{c.max}</span>
            <span className="text-xs font-mono w-44 shrink-0">{c.name}</span>
            <span className="text-[11px] text-text-muted">{c.note}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Faktor" value={k.faktor ?? "—"}
          sub={k.allin_faktor ? `All-in ${k.allin_faktor}` :
               r.rent.potential_faktor ? `Potenzial ${r.rent.potential_faktor}` : null} />
        <Stat label="€/m²" value={eur(k.price_m2)}
          sub={[
            k.mv_delta_pct != null ? `${k.mv_delta_pct > 0 ? "+" : ""}${k.mv_delta_pct}% vs. Median` : null,
            k.price_pro_we != null ? `${eur(k.price_pro_we)}/WE` : null,
          ].filter(Boolean).join(" · ") || null} />
        <Stat label="Bruttorendite" value={`${k.gross_yield_pct}%`} sub={`netto ${k.net_yield_pct}%`} />
        <Stat label="CF vor Steuer" value={`${fin.cf_pretax_mo} €/Mo`} accent={cfAccent}
          sub={`Stress @${(stx.rate * 100).toLocaleString("de-AT")}%: ${stx.cf_pretax_mo} €/Mo`} />
        <Stat label="Cash-on-Cash" value={k.coc_pct != null ? `${k.coc_pct}%` : "—"} />
        <Stat label="CF nach Steuer" value={`${fin.cf_aftertax_yr} €/J`} />
        <Stat label="Vermögenszuwachs" value={`${eur(k.vermoegenszuwachs_yr)}/J`} sub="CF n.St. + Tilgung" />
        <Stat label="Breakeven" value={r.breakeven_jahr != null ? `Jahr ${r.breakeven_jahr}` : "> 30J"}
          sub="bei 2% Mietwachstum" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-border bg-surface p-4">
          <div className="overline mb-2">
            Finanzierung @ {(fin.rate * 100).toLocaleString("de-AT")}% · LTV {Math.round(fin.ltv * 100)}% · {fin.term_years}J
          </div>
          <Row k="Effektivpreis (inkl. Makler)" v={eur(fin.eff)} />
          <Row k="Kaufnebenkosten" v={eur(fin.nk)} />
          <Row k="Darlehen" v={eur(fin.loan)} />
          <Row k="Cash-Bedarf (EK + NK + Reno + Umlagen)" v={eur(fin.cash_in)}
            accent={r.machbar == null ? undefined : r.machbar ? "text-signal-green" : "text-signal-red"} />
          {r.machbar != null && (
            <Row k="Machbarkeit vs. verfügbares EK" v={r.machbar ? "✓ machbar" : "✗ EK reicht nicht"}
              accent={r.machbar ? "text-signal-green" : "text-signal-red"} />
          )}
          <Row k="Annuität" v={`${eur(fin.mortgage_mo)}/Mo`} />
          <Row k={`OPEX (${fin.opex_src})`} v={`${eur(fin.opex_mo)}/Mo`} />
          <Row k={`Steuer p.a. (AfA ${(fin.afa_rate * 100).toLocaleString("de-AT")}%)`} v={eur(fin.tax_yr)} />
          <Row k={`Stress-Annuität @ ${(stx.rate * 100).toLocaleString("de-AT")}%-Anker`} v={`${eur(stx.mortgage_mo)}/Mo`} />
          <div className="mt-2 text-[10px] text-text-muted">
            Dein Zins treibt CF/Machbarkeit; Zonen & Screen bleiben 4,5%-verankert (Konzept §13.2).
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-border bg-surface p-4">
            <div className="overline mb-2">Miete</div>
            <Row k={`Ansatz (${r.rent.primary_src})`} v={`${r.rent.primary_m2} €/m² = ${eur(r.rent.primary_mo)}/Mo`} />
            {r.rent.calibrated_m2 != null && r.rent.vermietet && (
              <Row k={`Kalibriert (${r.rent.calibrated_src})`} v={`${r.rent.calibrated_m2} €/m²`} />
            )}
            {r.rent.potential_faktor != null && (
              <Row k="Potenzial-Faktor (kalibrierte Miete)" v={r.rent.potential_faktor} />
            )}
            {r.rent.rent_to_income?.flag && (
              <Row k="Miete vs. lokales Einkommen" v={`${r.rent.rent_to_income.rent_to_income_pct}% ⚠`} accent="text-signal-yellow" />
            )}
          </div>

          {r.marktwert && (
            <div className="border border-border bg-surface p-4">
              <div className="overline mb-2">Marktwertvergleich</div>
              <Row k="Median €/m²" v={eur(r.marktwert.median_m2)} />
              <Row k="p25 – p75" v={`${eur(r.marktwert.p25_m2)} – ${eur(r.marktwert.p75_m2)}`} />
              <Row k="Datenbasis" v={`n=${r.marktwert.n} · ${r.marktwert.window_days}d`} />
              <Row k="Vergleichsgröße"
                   v={r.marktwert.size_band
                        ? `${Math.round(r.marktwert.size_band[0])}–${Math.round(r.marktwert.size_band[1])} m²`
                        : "alle Größen"} />
              <div className="mt-2 text-[10px] text-text-muted">
                Angebotspreise — Transaktionen typischerweise ≈ −4%.
                {!r.marktwert.size_adjusted && " Zu wenige Vergleichsobjekte dieser Größe — "
                  + "Median über alle Größen, bei sehr kleinen/großen Objekten verzerrt."}
              </div>
            </div>
          )}
        </div>
      </div>

      {(r.mietrecht?.applies || r.mietrecht?.note) && (
        <div className="border border-border bg-surface p-4">
          <div className="overline mb-2">Mietrecht{r.mietrecht.as_of ? ` (Stand ${r.mietrecht.as_of})` : ""}</div>
          {r.mietrecht.applies ? (
            <>
              <Row k={`Richtwert ${r.mietrecht.bundesland}`} v={`${r.mietrecht.richtwert_m2} €/m²`} />
              <Row k={`+ Lagezuschlag ~${Math.round((r.mietrecht.lagezuschlag_assumed || 0) * 100)}% → Deckel`}
                v={`≈ ${r.mietrecht.ceiling_m2_est} €/m²`} />
              <Row k="MRG-Risiko (Markt)" v={r.mietrecht.mrg_risk} />
              {r.mietrecht.locked && (
                <Row k="Bestand" v="GEBUNDEN — Altmieter unbefristet" accent="text-signal-red" />
              )}
              {r.mietrecht.anfechtungsrisiko_eur > 0 && (
                <Row k="Anfechtungsrisiko (36 Mo)" v={eur(r.mietrecht.anfechtungsrisiko_eur)}
                  accent="text-signal-yellow" />
              )}
              <div className="mt-2 text-[10px] text-text-muted">
                {r.mietrecht.lagezuschlag_note} · {r.mietrecht.mietpreisbremse}
              </div>
            </>
          ) : (
            <div className="text-xs font-mono text-text-secondary">{r.mietrecht.note}</div>
          )}
        </div>
      )}

      {r.mikro && (
        <div className="border border-border bg-surface p-4">
          <div className="overline mb-2">Mikrolage v1 · display-only, fließt nicht in den Score</div>
          {r.mikro.ok ? (
            <>
              {r.mikro.distances_m ? (
                <div className="grid grid-cols-3 gap-3">
                  {[["oev_halt", "ÖV-Halt"], ["bahnhof", "Bahnhof"], ["supermarkt", "Supermarkt"]]
                    .filter(([key]) => r.mikro.distances_m[key] != null)
                    .map(([key, lbl]) => (
                      <Row key={key} k={lbl} v={`${r.mikro.distances_m[key]} m`} />
                    ))}
                </div>
              ) : (
                <div className="text-xs font-mono text-signal-yellow">⚠ {r.mikro.reason || "keine OSM-Treffer"}</div>
              )}
              {r.mikro.laerm_markt && (
                <Row k="Lärm (Markt-Ebene)" v={r.mikro.laerm_markt.label} />
              )}
              <div className="mt-2 text-[11px] font-mono text-signal-yellow">
                ☐ {r.mikro.flood?.hint}
              </div>
              <div className="mt-1 text-[10px] text-text-muted">
                {r.mikro.resolved} · {r.mikro.source}
              </div>
            </>
          ) : (
            <div className="text-xs font-mono text-signal-yellow">✗ {r.mikro.reason}</div>
          )}
        </div>
      )}

      {(r.vermoegensverlauf || []).length > 0 && (
        <div className="border border-border bg-surface p-4">
          <div className="overline mb-2">
            Vermögensverlauf — EK bei 0 / 1 / 2 % Wert p.a. (Szenarioband, keine Prognose)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-text-muted text-left">
                  <th className="py-1 pr-3">Jahr</th><th className="pr-3">Restschuld</th>
                  <th className="pr-3">Tilgung kum.</th><th className="pr-3">EK 0 %</th>
                  <th className="pr-3">EK 1 %</th><th>EK 2 %</th>
                </tr>
              </thead>
              <tbody>
                {r.vermoegensverlauf
                  .filter((row) => [1, 5, 10, 15, 20, 25, 30].includes(row.jahr))
                  .map((row) => (
                    <tr key={row.jahr} className="border-t border-border/50">
                      <td className="py-1 pr-3">{row.jahr}</td>
                      <td className="pr-3">{eur(row.restschuld)}</td>
                      <td className="pr-3">{eur(row.tilgung_kum)}</td>
                      <td className="pr-3">{eur(row.ek_0pct)}</td>
                      <td className="pr-3">{eur(row.ek_1pct)}</td>
                      <td>{eur(row.ek_2pct)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {r.chancen_risiken && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-signal-greenBorder bg-surface p-4">
            <div className="overline mb-2 text-signal-green">Chancen</div>
            {(r.chancen_risiken.chancen.length ? r.chancen_risiken.chancen
              : [{ text: "— keine erkannt —", signal: "engine" }]).map((c, i) => (
              <div key={i} className="py-1.5 border-b border-border/50 last:border-0">
                <div className="text-xs">{c.text}</div>
                <div className="text-[10px] font-mono text-text-muted">← {c.signal}</div>
              </div>
            ))}
          </div>
          <div className="border border-signal-redBorder bg-surface p-4">
            <div className="overline mb-2 text-signal-red">Risiken</div>
            {(r.chancen_risiken.risiken.length ? r.chancen_risiken.risiken
              : [{ text: "— keine erkannt —", signal: "engine" }]).map((c, i) => (
              <div key={i} className="py-1.5 border-b border-border/50 last:border-0">
                <div className="text-xs">{c.text}</div>
                <div className="text-[10px] font-mono text-text-muted">← {c.signal}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-signal-greenBorder bg-surface p-4">
        <div className="overline mb-2">Preisempfehlung</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Row k={emp.bid_zone_note} v={eur(emp.bid_zone_green)} />
          <Row k="Marktwert-fair (adjustiert)" v={eur(emp.mv_fair)} />
          <Row k="CF-neutral (dein Zins)" v={eur(emp.cf_neutral)} />
        </div>
        {emp.mv_adjustments?.length > 0 && (
          <div className="mt-2 text-[10px] font-mono text-text-muted">
            Basis {eur(emp.mv_fair_base)} · {emp.mv_adjustments.map((a) =>
              `${a.label} ${a.pct != null ? `${a.pct}%` : eur(a.eur)}`).join(" · ")}
          </div>
        )}
        {emp.mv_fair_p25 != null && (
          <div className="mt-2 text-[10px] text-text-muted">
            Marktwert-Spanne p25–p75{" "}
            <span className="font-mono">{eur(emp.mv_fair_p25)} – {eur(emp.mv_fair_p75)}</span>
            {emp.mv_spread != null && ` (Streuung ${emp.mv_spread.toFixed(2)}×`}
            {emp.mv_confidence != null && emp.mv_confidence < 1
              && `, Gewicht im Score ${Math.round(emp.mv_confidence * 100)} %`}
            {emp.mv_spread != null && ")"}
            {" — "}die Streuung ist, was das Modell nicht sieht: Zustand, Stockwerk,
            Aussicht. Die Besichtigung entscheidet, wo in der Spanne dieses Objekt liegt;
            die Empfehlung selbst folgt dem Median.
          </div>
        )}
        {emp.empfehlung != null && (
          <div className="mt-3 num text-2xl text-signal-green">
            → Gebot bis ~{eur(emp.empfehlung)}
            <span className="text-sm text-text-secondary ml-3">({emp.delta_vs_ask_pct}% vs. Ask)</span>
          </div>
        )}
      </div>

      {(r.chips.length > 0 || r.notes.length > 0) && (
        <div className="space-y-1">
          {r.chips.map((c, i) => (
            <div key={i} className="text-xs font-mono text-signal-yellow">⚠ {c}</div>
          ))}
          {r.notes.map((n, i) => (
            <div key={i} className="text-xs font-mono text-text-muted">ℹ {n}</div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-text-muted">
        Indikativ. Keine Anlage-/Steuer-/Rechtsberatung. Human-DD bleibt Pflicht (PLAYBOOK) —
        Engine: {r.engine}, identisch zur CLI (`python bewerten.py`).
      </div>
    </div>
  );
}

export default function Analyze() {
  const [form, setForm] = useState(EMPTY);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [markets, setMarkets] = useState([]);
  const [openDetail, setOpenDetail] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [importMode, setImportMode] = useState("url");
  const [importUrl, setImportUrl] = useState("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [lastPayload, setLastPayload] = useState(null);
  const [deals, setDeals] = useState([]);
  const [dealSavedId, setDealSavedId] = useState(null);
  const [docDealId, setDocDealId] = useState("");
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [docUploading, setDocUploading] = useState(false);
  const [docResult, setDocResult] = useState(null);
  const [scOv, setScOv] = useState({ rate: "", rent_actual_mo: "", reno_cost: "", restnutzwert_jahre: "" });
  const [scDiff, setScDiff] = useState(null);
  const [scBusy, setScBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useI18n();
  // Display only. The profile field deliberately stays EMPTY so bewerten.py's
  // InvestorProfile owns the default (single source of truth) — the live band
  // is shown as context for what to type, never written into the field.
  const rateDefault = useRateDefault(0.038);
  const listingId = params.get("id");

  const refreshDeals = () =>
    api.get("/analyze/deals").then(({ data }) => setDeals(data.deals || [])).catch(() => {});

  useEffect(() => {
    api.get("/markets")
      // /markets returns { markets: [...] } — reading it as a bare array left the
      // dropdown permanently empty, forcing every web analysis into degraded mode.
      .then(({ data }) => setMarkets(Array.isArray(data) ? data : (data?.markets || [])))
      .catch(() => setMarkets([]));
  }, []);

  const applyPrefill = (data) => {
    setPrefill(data);
    if (!data?.fields) return;
    const patch = {};
    Object.entries(data.fields).forEach(([k, v]) => {
      if (k in EMPTY) patch[k] = typeof v === "boolean" ? v : String(v);
    });
    setForm((s) => ({ ...s, ...patch }));
    if (data.fields.baujahr || data.fields.energieklasse || data.fields.verwaltung_mo) {
      setOpenDetail(true);
    }
  };

  useEffect(() => {
    const id = params.get("id");
    if (id) {
      api.post("/analyze/prefill", { listing_id: id })
        .then(({ data }) => applyPrefill(data))
        .catch(() => {});
    }
    api.get("/analyze/profile").then(({ data }) => {
      if (data?.profile) {
        setProfile((s) => {
          const patch = {};
          Object.keys(EMPTY_PROFILE).forEach((k) => {
            if (data.profile[k] != null) {
              patch[k] = k === "gmbh" ? Boolean(data.profile[k]) : String(data.profile[k]);
            }
          });
          return { ...s, ...patch };
        });
      }
    }).catch(() => {});
    refreshDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runImport = async (endpoint, body) => {
    setImporting(true);
    setPrefill(null);
    try {
      const { data } = await api.post(endpoint, body);
      applyPrefill(data);
    } catch (err) {
      setPrefill({ found: false,
        warnings: [err?.response?.data?.detail || "Import fehlgeschlagen"] });
    } finally {
      setImporting(false);
    }
  };

  const doImport = () => importUrl && runImport("/analyze/prefill", { url: importUrl });
  const doImportText = () => importText.trim() && runImport("/analyze/extract", { text: importText });

  const doImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setPrefill({ found: false, warnings: ["Datei > 8 MB — bitte verkleinern"] });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(",", 2)[1] || "";
      const mediaType = file.type === "image/jpg" ? "image/jpeg" : file.type;
      runImport("/analyze/extract", { file_b64: b64, media_type: mediaType });
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    const prof = {};
    PROFILE_NUM.forEach((f) => {
      if (profile[f] !== "" && profile[f] != null) {
        const v = Number(profile[f]);
        if (!Number.isNaN(v)) prof[f] = v;
      }
    });
    if (profile.ziel) prof.ziel = profile.ziel;
    prof.gmbh = !!profile.gmbh;
    try {
      await api.put("/analyze/profile", prof);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Profil speichern fehlgeschlagen");
    }
  };

  const set = (f) => (e) =>
    setForm((s) => ({ ...s, [f]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setP = (f) => (e) => setProfile((s) => ({ ...s, [f]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = buildPayload(form, profile);
      const { data } = await api.post("/analyze", payload);
      setResult(data);
      setLastPayload(payload);
      setDealSavedId(null);
      setScDiff(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Analyse fehlgeschlagen — Eingaben prüfen.");
    } finally {
      setLoading(false);
    }
  };

  const saveDeal = () => {
    if (!lastPayload) return;
    api.post("/analyze/deals", lastPayload)
      .then(({ data }) => { setDealSavedId(data.deal_id); setDocDealId(data.deal_id); refreshDeals(); })
      .catch((err) => setError(err?.response?.data?.detail || "Speichern fehlgeschlagen"));
  };

  const loadDeal = (id) => {
    api.get(`/analyze/deals/${id}`).then(({ data }) => {
      const patch = {};
      Object.entries(data.payload || {}).forEach(([k, v]) => {
        if (k in EMPTY) patch[k] = typeof v === "boolean" ? v : String(v);
      });
      setForm({ ...EMPTY, ...patch });
      setResult(data.result);
      setLastPayload(data.payload);
      setDealSavedId(data.deal_id);
      setDocDealId(data.deal_id);
      setScDiff(null);
    }).catch(() => {});
  };

  const uploadObjectDoc = async (file) => {
    if (!file) return;
    setDocUploading(true);
    setDocResult(null);
    try {
      const fd = new FormData();
      fd.append("document_type", docType);
      if (docDealId) fd.append("deal_id", docDealId);
      fd.append("file", file);
      const { data } = await api.post("/analyze/documents/upload", fd);
      setDocResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Dokument-Upload fehlgeschlagen");
    } finally {
      setDocUploading(false);
    }
  };

  const removeDeal = (id) => {
    api.delete(`/analyze/deals/${id}`)
      .then(() => setDeals((s) => s.filter((d) => d.deal_id !== id)))
      .catch(() => {});
  };

  const runScenario = () => {
    if (!lastPayload) return;
    const ov = {};
    if (scOv.rent_actual_mo) ov.rent_actual_mo = Number(scOv.rent_actual_mo);
    if (scOv.reno_cost) ov.reno_cost = Number(scOv.reno_cost);
    if (scOv.restnutzwert_jahre) ov.restnutzwert_jahre = Number(scOv.restnutzwert_jahre);
    if (scOv.rate) ov.profile = { rate: Number(scOv.rate) };
    if (!Object.keys(ov).length) return;
    setScBusy(true);
    setScDiff(null);
    api.post("/analyze/scenario",
      dealSavedId ? { deal_id: dealSavedId, overrides: ov } : { base: lastPayload, overrides: ov })
      .then(({ data }) => setScDiff(data))
      .catch((err) => setError(err?.response?.data?.detail || "Szenario fehlgeschlagen"))
      .finally(() => setScBusy(false));
  };

  const marketOpts = markets
    .map((m) => ({ code: m.market_code || m.code || m.market, name: m.market_name || m.name }))
    .filter((m) => m.code)
    .sort((a, b) => a.code.localeCompare(b.code));

  return (
    <div className="space-y-6" data-testid="bewerter-page">
      <div>
        {listingId && (
          <button
            onClick={() => nav(`/listings/${listingId}`)}
            className="overline hover:text-white inline-flex items-center gap-2 mb-3"
            data-testid="analyze-back-to-listing"
          >
            <ArrowLeft size={12} /> {t("common.backToListing")}
          </button>
        )}
        <h1 className="font-heading text-2xl flex items-center gap-2">
          <Crosshair size={20} /> Bewerter
        </h1>
        <p className="text-xs text-text-muted mt-1">
          Unkalibrierte Märkte brauchen eine Ist-Miete.
        </p>
      </div>

      <div className="border border-border bg-surface p-4 space-y-3 no-print" data-testid="bw-import">
        <div className="overline flex items-center gap-2"><Link2 size={12} /> Inserat-Import (optional)</div>
        <div className="inline-flex border border-border font-mono text-[10px] uppercase tracking-widest2">
          {[["url", "URL"], ["text", "Text"], ["file", "PDF/Bild"]].map(([m, label]) => (
            <button key={m} type="button" onClick={() => setImportMode(m)}
              className={`px-3 py-1 transition-colors ${importMode === m
                ? "bg-white text-black" : "text-text-muted hover:text-white"}`}
              data-testid={`bw-import-tab-${m}`}>
              {label}
            </button>
          ))}
        </div>
        {importMode === "url" && (
          <div className="flex gap-2">
            <Input placeholder="https://www.willhaben.at/iad/immobilien/…" value={importUrl}
              onChange={(e) => setImportUrl(e.target.value)} data-testid="bw-import-url" />
            <Button type="button" variant="ghost" onClick={doImport}
              disabled={importing || !importUrl} data-testid="bw-import-go">
              {importing ? <Spinner size={13} /> : null} Daten übernehmen
            </Button>
          </div>
        )}
        {importMode === "text" && (
          <div className="space-y-2">
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)}
              rows={6} placeholder="Exposé-Text hier einfügen…"
              className="w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none"
              data-testid="bw-import-text" />
            <Button type="button" variant="ghost" onClick={doImportText}
              disabled={importing || !importText.trim()}>
              {importing ? <Spinner size={13} /> : null} Text extrahieren
            </Button>
          </div>
        )}
        {importMode === "file" && (
          <div className="space-y-2">
            <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={doImportFile} disabled={importing}
              className="block text-xs font-mono text-text-secondary file:mr-3 file:border file:border-zinc-700 file:bg-transparent file:px-3 file:py-1.5 file:text-xs file:font-mono file:uppercase file:text-white file:cursor-pointer"
              data-testid="bw-import-file" />
            {importing && <Spinner size={13} />}
            <div className="text-[10px] text-text-muted">
              Bot-gesperrte Portale (z. B. IS24.de): Inserat drucken → als PDF speichern → hier hochladen.
            </div>
          </div>
        )}
        <div className="text-[10px] text-text-muted">
          URL: willhaben Korpus + Live · Text/PDF: Claude-Extraktion mit Beleg-Prüfung
          (€-Anker, Zitat-Abgleich) — jede Übernahme unten bestätigen, nichts geht ungeprüft in die Engine.
        </div>
        {prefill && (
          <div className="space-y-1">
            <div className={`text-xs font-mono ${prefill.found ? "text-signal-green" : "text-signal-yellow"}`}>
              {prefill.found
                ? `✓ übernommen (${prefill.source || "—"})${prefill.title ? ` · ${prefill.title}` : ""}`
                : "nichts gefunden — Felder manuell ausfüllen"}
            </div>
            {(prefill.warnings || []).map((w, i) => (
              <div key={i} className="text-[11px] font-mono text-signal-yellow">⚠ {w}</div>
            ))}
          </div>
        )}
      </div>

      {deals.length > 0 && (
        <div className="border border-border bg-surface p-4 no-print" data-testid="bw-deals">
          <div className="overline mb-2">Gespeicherte Deals ({deals.length})</div>
          {deals.map((dl) => (
            <div key={dl.deal_id}
              className="flex items-center gap-3 py-1 border-b border-border/50 last:border-0">
              <button type="button" onClick={() => loadDeal(dl.deal_id)}
                className="text-xs font-mono text-white hover:text-signal-green text-left flex-1">
                Deal {dl.deal_id} · {dl.title} · {dl.score}/100 {dl.ampel}
                {dl.n_scenarios > 0 ? ` · ${dl.n_scenarios} Szenarien` : ""}
              </button>
              <span className="text-[10px] text-text-muted">{(dl.updated_at || "").slice(0, 10)}</span>
              <a href={`${BACKEND_URL}/api/analyze/deals/${dl.deal_id}/export.md`}
                className="text-[10px] font-mono text-text-muted hover:text-white" title="Dossier .md">md</a>
              <button type="button" onClick={() => removeDeal(dl.deal_id)} title="löschen"
                className="text-[10px] font-mono text-text-muted hover:text-signal-red">✕</button>
            </div>
          ))}
        </div>
      )}

      <div className="border border-border bg-surface p-4 space-y-3 no-print" data-testid="bw-doc-upload">
        <div className="overline">Objektunterlagen</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="overline block mb-1">Dokumenttyp</span>
            <Select value={docType} onChange={(e) => setDocType(e.target.value)}>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </label>
          <label className="block md:col-span-2">
            <span className="overline block mb-1">Deal-Ziel</span>
            <Select value={docDealId} onChange={(e) => setDocDealId(e.target.value)}>
              <option value="">— kein Deal verknüpfen —</option>
              {deals.map((dl) => (
                <option key={dl.deal_id} value={dl.deal_id}>
                  {dl.title || dl.deal_id} · {dl.score}/100 {dl.ampel}
                </option>
              ))}
            </Select>
          </label>
          <label className="block md:col-span-3">
            <span className="overline block mb-1">Datei</span>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              disabled={docUploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                if (file.size > 16 * 1024 * 1024) {
                  setError("Datei > 16 MB — bitte verkleinern");
                  return;
                }
                uploadObjectDoc(file);
              }}
              className="block text-xs font-mono text-text-secondary file:mr-3 file:border file:border-zinc-700 file:bg-transparent file:px-3 file:py-1.5 file:text-xs file:font-mono file:uppercase file:text-white file:cursor-pointer"
              data-testid="bw-object-doc-file"
            />
          </label>
        </div>
        <div className="text-[10px] text-text-muted">
          Unterstützt: Energieausweis, Grundbuch, Mietvertrag, Nutzwertgutachten, Objektabrechnungen,
          Bestandspläne, ETV-Protokolle, Monatsvorschreibungen, Wohnungseigentumsvertrag.
        </div>
        {docUploading && <Spinner size={13} />}
        {docResult && (
          <div className="space-y-3 border border-border bg-bg p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-mono text-signal-green">
                ✓ {docResult.document?.document_type || docType} übernommen
              </div>
              {docResult.document?.document_id && (
                <a
                  href={`${BACKEND_URL}/api/analyze/documents/${docResult.document.document_id}`}
                  className="text-[10px] font-mono text-text-muted hover:text-white"
                  title="Dokumentdetails"
                >
                  json
                </a>
              )}
            </div>
            {(docResult.warnings || []).map((w, i) => (
              <div key={i} className="text-[11px] font-mono text-signal-yellow">⚠ {w}</div>
            ))}
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="overline mb-1">Normalized fields</div>
                <pre className="max-h-64 overflow-auto text-[10px] font-mono whitespace-pre-wrap bg-bg border border-border p-2">
                  {JSON.stringify(docResult.fields || {}, null, 2)}
                </pre>
              </div>
              <div>
                <div className="overline mb-1">Vault copy</div>
                <pre
                  className="max-h-64 overflow-auto text-[10px] font-mono whitespace-pre-wrap bg-bg border border-border p-2"
                  data-testid="doc-vault-copy"
                >
                  {JSON.stringify(docResult.vault_copy || {}, null, 2)}
                </pre>
              </div>
            </div>
            {docResult.ocr_text && (
              <details>
                <summary className="cursor-pointer text-xs font-mono text-white">OCR text</summary>
                <pre className="mt-2 max-h-72 overflow-auto text-[10px] font-mono whitespace-pre-wrap bg-bg border border-border p-2">
                  {docResult.ocr_text}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-4 no-print">
        <div className="inline-flex border border-border font-mono text-[10px] uppercase tracking-widest2">
          {[["etw", "Eigentumswohnung"], ["mfh", "Mehrfamilienhaus"]].map(([t, label]) => (
            <button key={t} type="button"
              onClick={() => setForm((s) => ({ ...s, objekt_typ: t }))}
              className={`px-3 py-1 transition-colors ${form.objekt_typ === t
                ? "bg-white text-black" : "text-text-muted hover:text-white"}`}
              data-testid={`bw-typ-${t}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="border border-border bg-surface p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Kaufpreis € *">
            <Input type="number" required min="1" step="1000" value={form.price} onChange={set("price")} data-testid="bw-price" />
          </Field>
          <Field label={form.objekt_typ === "mfh" ? "Nettomietfläche m² *" : "Wohnfläche m² *"}>
            <Input type="number" required min="1" step="0.5" value={form.size_m2} onChange={set("size_m2")} data-testid="bw-size" />
          </Field>
          {form.objekt_typ === "mfh" && (
            <>
              <Field label="Jahresnettokaltmiete €/a *" hint="Ist-Miete des Hauses (aggregiert)">
                <Input type="number" required min="1" value={form.jahresnettokaltmiete}
                  onChange={set("jahresnettokaltmiete")} data-testid="bw-jahresnetto" />
              </Field>
              <Field label="Anzahl WE">
                <Input type="number" min="1" value={form.anzahl_we} onChange={set("anzahl_we")} />
              </Field>
              <Field label="Leerstand m²" hint="vermietbare, derzeit leere Fläche">
                <Input type="number" min="0" value={form.leerstand_m2} onChange={set("leerstand_m2")} />
              </Field>
              <Field label="Gewerbefläche m²" hint="≥30 % → Bank-Warnung">
                <Input type="number" min="0" value={form.gewerbe_m2} onChange={set("gewerbe_m2")} />
              </Field>
            </>
          )}
          <Field label="Markt" hint="leer = unkalibriert (PLZ oder Land/Lage setzen)">
            <Select value={form.market} onChange={set("market")}>
              <option value="">— kein konfigurierter Markt —</option>
              {marketOpts.map((m) => (
                <option key={m.code} value={m.code}>{m.code}{m.name ? ` · ${m.name}` : ""}</option>
              ))}
            </Select>
          </Field>
          <Field label="PLZ" hint="optional — löst Markt & Kernlage auf">
            <Input value={form.postcode} onChange={set("postcode")} />
          </Field>
          <Field label="Adresse" hint="optional — aktiviert Mikro v1 (OSM, display-only)">
            <Input value={form.address} onChange={set("address")} placeholder="Straße Nr." />
          </Field>
          {form.objekt_typ !== "mfh" && (
            <>
              <Field label="Ist-Nettokaltmiete €/Mo" hint="Pflicht bei unkalibriertem Markt">
                <Input type="number" min="0" step="10" value={form.rent_actual_mo} onChange={set("rent_actual_mo")} data-testid="bw-rent" />
              </Field>
              <div className="flex flex-col gap-2 justify-end pb-1">
                <Check label="vermietet" checked={form.vermietet} onChange={set("vermietet")} />
                <Check label="unbefristet (AT Altmieter)" checked={form.unbefristet} onChange={set("unbefristet")} />
              </div>
              <Field label="Befristet bis" hint="AT, z.B. 2028-12">
                <Input value={form.befristet_bis} onChange={set("befristet_bis")} placeholder="YYYY-MM" />
              </Field>
            </>
          )}
          <Field label="Verkäufer">
            <Select value={form.seller} onChange={set("seller")}>
              <option value="makler">Makler (Provision eingerechnet)</option>
              <option value="privat">privat / provisionsfrei</option>
            </Select>
          </Field>
        </div>

        <Section title="Detailfelder" open={openDetail} onToggle={() => setOpenDetail(!openDetail)}>
          <Field label="Baujahr"><Input type="number" value={form.baujahr} onChange={set("baujahr")} /></Field>
          <Field label="Energieklasse">
            <Select value={form.energieklasse} onChange={set("energieklasse")}>
              <option value="">—</option>
              {["A", "B", "C", "D", "E", "F", "G", "H"].map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Stockwerk (0 = EG)" hint="FF: ab 3. Stock nur mit Lift">
            <Input type="number" min="0" value={form.stockwerk} onChange={set("stockwerk")} />
          </Field>
          <Field label="Aufzug">
            <Select value={form.aufzug} onChange={set("aufzug")}>
              <option value="">—</option>
              <option value="ja">ja</option>
              <option value="nein">nein</option>
            </Select>
          </Field>
          <Field label="Hausgeld ges. €/Mo"><Input type="number" value={form.hausgeld_mo} onChange={set("hausgeld_mo")} /></Field>
          <Field label="↳ Instandhaltungsrücklage €/Mo"><Input type="number" value={form.ihr_mo} onChange={set("ihr_mo")} /></Field>
          <Field label="↳ Verwaltung €/Mo"><Input type="number" value={form.verwaltung_mo} onChange={set("verwaltung_mo")} /></Field>
          <Field label="BK-Vorauszahlung Mieter €/Mo"><Input type="number" value={form.bk_voraus_mo} onChange={set("bk_voraus_mo")} /></Field>
          <Field label="Anteil Liegenschaft (0–1)"><Input type="number" step="0.001" value={form.anteil} onChange={set("anteil")} placeholder="0.03" /></Field>
          <Field label="Sonderumlagen € (eigener Anteil)"><Input type="number" value={form.sonderumlagen} onChange={set("sonderumlagen")} /></Field>
          <Field label="Sanierung Wohnung €"><Input type="number" value={form.reno_cost} onChange={set("reno_cost")} /></Field>
          <Field label="Restnutzwert-Gutachten (Jahre)" hint="beschleunigte AfA, ≥20J">
            <Input type="number" min="20" max="80" value={form.restnutzwert_jahre} onChange={set("restnutzwert_jahre")} />
          </Field>
          <Field label="Land (unkalibriert)">
            <Select value={form.country} onChange={set("country")}>
              <option value="">—</option><option value="AT">AT</option><option value="DE">DE</option>
            </Select>
          </Field>
          <Field label="Lage-Note (unkalibriert)">
            <Select value={form.lage} onChange={set("lage")}>
              <option value="">— Default B —</option>
              {["A", "A-", "B+", "B", "B-", "C"].map((g) => <option key={g} value={g}>{g}</option>)}
            </Select>
          </Field>
          <div className="flex items-end pb-1">
            <Check label="Erbbaurecht / Baurecht (K.O.)" checked={form.erbbaurecht} onChange={set("erbbaurecht")} />
          </div>
        </Section>

        <Section title="Deine Situation (leer = Engine-Defaults)" open={openProfile} onToggle={() => setOpenProfile(!openProfile)}>
          <Field label="Zinssatz" hint={`Engine-Default 0.038 · ${rateHint(rateDefault, t)}`}><Input type="number" step="0.001" value={profile.rate} onChange={setP("rate")} placeholder="0.038" /></Field>
          <Field label="EK-Anteil" hint="Default 0.20 → LTV 80%"><Input type="number" step="0.01" value={profile.ek_anteil} onChange={setP("ek_anteil")} placeholder="0.20" /></Field>
          <Field label="Laufzeit Jahre" hint="Default 30"><Input type="number" value={profile.term_years} onChange={setP("term_years")} placeholder="30" /></Field>
          <Field label="Grenzsteuersatz" hint="Default 0.42"><Input type="number" step="0.01" value={profile.steuersatz} onChange={setP("steuersatz")} placeholder="0.42" /></Field>
          <Field label="Verfügbares EK €" hint="für Machbarkeits-Check"><Input type="number" value={profile.ek_available} onChange={setP("ek_available")} /></Field>
          <Field label="Investmentziel">
            <Select value={profile.ziel} onChange={setP("ziel")}>
              <option value="">— Default cashflow —</option>
              <option value="cashflow">Cashflow</option>
              <option value="vermoegensaufbau">Vermögensaufbau</option>
              <option value="steuer">Steueroptimierung</option>
              <option value="vorsorge">Altersvorsorge</option>
            </Select>
          </Field>
          <Field label="Haltedauer Jahre" hint="Default 15"><Input type="number" value={profile.haltedauer_jahre} onChange={setP("haltedauer_jahre")} placeholder="15" /></Field>
          <div className="flex items-end pb-1">
            <Check label="GmbH (KöSt-Näherung)" checked={!!profile.gmbh}
              onChange={(e) => setProfile((s) => ({ ...s, gmbh: e.target.checked }))} />
          </div>
          <div className="col-span-2 md:col-span-4 flex items-center gap-3">
            <Button type="button" variant="ghost" onClick={saveProfile} data-testid="bw-save-profile">
              Profil speichern
            </Button>
            {profileSaved && <span className="text-xs font-mono text-signal-green">✓ gespeichert</span>}
            <span className="text-[10px] text-text-muted">wird beim nächsten Laden automatisch eingesetzt</span>
          </div>
        </Section>

        <div className="flex items-center gap-4">
          <Button type="submit" variant="green" disabled={loading} data-testid="bw-submit">
            {loading ? <Spinner size={14} /> : null} Deal analysieren
          </Button>
          {error && (
            <div className="text-xs font-mono text-signal-red whitespace-pre-line" data-testid="bw-error">
              {String(error)}
            </div>
          )}
        </div>
      </form>

      {result && (
        <>
          <Dossier r={result} />
          <div className="border border-border bg-surface p-4 space-y-3 no-print" data-testid="bw-actions">
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" variant="ghost" onClick={saveDeal}
                disabled={!lastPayload || !!dealSavedId} data-testid="bw-save-deal">
                {dealSavedId ? "✓ gespeichert" : "Deal speichern"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => window.print()}
                data-testid="bw-print">
                Drucken / PDF
              </Button>
              {dealSavedId && (
                <>
                  <span className="text-xs font-mono text-signal-green">
                    ✓ gespeichert — im IMMO-AI-Chat als Deal {dealSavedId} referenzierbar
                  </span>
                  <a href={`${BACKEND_URL}/api/analyze/deals/${dealSavedId}/export.md`}
                    className="text-xs font-mono text-white underline hover:text-signal-green"
                    data-testid="bw-export-md">
                    Dossier als .md exportieren
                  </a>
                </>
              )}
            </div>
            <div className="overline">Szenario testen — deterministischer Engine-Re-Run</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Field label="Zins" hint={rateHint(rateDefault, t)}>
                <Input type="number" step="0.001" value={scOv.rate}
                  onChange={(e) => setScOv((s) => ({ ...s, rate: e.target.value }))} />
              </Field>
              <Field label="NKM €/Mo">
                <Input type="number" value={scOv.rent_actual_mo}
                  onChange={(e) => setScOv((s) => ({ ...s, rent_actual_mo: e.target.value }))} />
              </Field>
              <Field label="Sanierung €">
                <Input type="number" value={scOv.reno_cost}
                  onChange={(e) => setScOv((s) => ({ ...s, reno_cost: e.target.value }))} />
              </Field>
              <Field label="Restnutzwert J">
                <Input type="number" value={scOv.restnutzwert_jahre}
                  onChange={(e) => setScOv((s) => ({ ...s, restnutzwert_jahre: e.target.value }))} />
              </Field>
              <div className="flex items-end pb-1">
                <Button type="button" variant="ghost" onClick={runScenario}
                  disabled={scBusy} data-testid="bw-scenario-go">
                  {scBusy ? <Spinner size={13} /> : null} Re-Run
                </Button>
              </div>
            </div>
            {scDiff && (
              <div data-testid="bw-scenario-diff">
                <div className="text-xs font-mono text-text-secondary mb-1">
                  Basis Score {scDiff.base_score} ({scDiff.base_ampel}) → Szenario{" "}
                  {scDiff.scenario?.score} ({scDiff.scenario?.ampel})
                </div>
                {(scDiff.diff || []).length === 0 ? (
                  <div className="text-xs font-mono text-text-muted">
                    keine Änderung der Kern-Kennzahlen
                  </div>
                ) : (
                  (scDiff.diff || []).map((d, i) => (
                    <div key={i}
                      className="flex items-baseline gap-3 py-1 border-b border-border/50 last:border-0 text-xs">
                      <span className="w-52 shrink-0 font-mono">{d.label}</span>
                      <span className="num">{String(d.base)}</span>
                      <span className="text-text-muted">→</span>
                      <span className="num text-white">{String(d.scenario)}</span>
                      {d.delta != null && (
                        <span className={`num ${d.delta >= 0 ? "text-signal-green" : "text-signal-red"}`}>
                          ({d.delta >= 0 ? "+" : ""}{d.delta})
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
